import { type HmacSigner, type HmacVerifier } from "@next-platform/packages-ts-types";

/**
 * Result of `createWebCryptoHmac` — the DI pair expected by
 * `configureAttribution`.
 */
export interface IWebCryptoHmac {
  signer: HmacSigner;
  verifier: HmacVerifier;
}

/**
 * Builds an `HmacSigner` / `HmacVerifier` pair backed by Web Crypto
 * (`crypto.subtle`), using HMAC-SHA-256 over the supplied `secret`.
 *
 * Browser-only: lives in the frontend rather than the cross-platform
 * `spicychat-business-logic` shim because React Native ships without
 * `crypto.subtle` by default — native will inject its own equivalent
 * (`expo-crypto` or similar) via `configureAttribution` when it
 * adopts the attribution host.
 *
 * Returns `undefined` when `crypto.subtle` is unavailable (very old
 * browsers) so callers can fall back to unsigned handoff without
 * branching on runtime availability.
 *
 * The underlying `CryptoKey` is cached by the returned closures so that
 * each sign/verify call avoids re-importing the key.
 */
export async function createWebCryptoHmac(secret: Uint8Array): Promise<IWebCryptoHmac | undefined> {
  if (typeof globalThis.crypto === "undefined" || typeof globalThis.crypto.subtle === "undefined") {
    return undefined;
  }

  const subtle = globalThis.crypto.subtle;
  const key = await subtle.importKey("raw", secret as BufferSource, { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);

  const signer: HmacSigner = async (message) => {
    const signature = await subtle.sign("HMAC", key, message as BufferSource);
    return new Uint8Array(signature);
  };

  const verifier: HmacVerifier = async (message, signature) => {
    return subtle.verify("HMAC", key, signature as BufferSource, message as BufferSource);
  };

  return { signer, verifier };
}
